import {
    Component,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { FileMetaData } from '@modeso/types__aumico-lib-companies-be';
import { I18NextService } from 'angular-i18next';
import { format, isValid } from 'date-fns';
import { DocumentModel } from '../../../../../aumico-lib-cpmm-fe/src/lib/interfaces/document.interface';
import { NavigationItem } from '../tab-navigation/tab-navigation.component';

// Interface for the combined document submission data
export interface DocumentSubmissionData {
    document: Partial<DocumentModel>;
    file?: File; // Optional file for upload
    isNew: boolean; // True if new upload, false if edit
}

@Component({
    selector: 'ham-document-form',
    templateUrl: './ham-document-form.component.html',
    styleUrls: ['./ham-document-form.component.scss'],
})
export class HamDocumentFormComponent implements OnInit, OnChanges {
    @Input() acceptedExtensions: string[] = [];
    @Input() maxSizeInMb: number;
    @Input() document: Partial<DocumentModel>;

    @Output() fileSizeError = new EventEmitter<boolean>();
    @Output() documentSubmit = new EventEmitter<DocumentSubmissionData>();
    @Output() onCancel = new EventEmitter<void>();

    public fileExtensionError = false;

    // Validation error properties
    public urlValidationError = false;
    public fileRequiredError = false;
    public urlRequiredError = false;
    public nameRequiredError = false;

    // File upload properties (single file only)
    public selectedFile: File | null = null;
    public fileMetaData: Partial<FileMetaData> | null = null;

    // Form values
    public documentName = '';
    public externalLinkUrl = '';

    // Edit mode properties
    public isEditMode = false;
    public isLinkDocument = false;
    public isFileDocument = false;

    public selectedTab = 0;
    public navigationItems: NavigationItem[] = [];
    public tabsTitles: string[] = [];

    public isPermanentFile = false;
    public dateFlyoutOpened = false;
    public flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };

    private _expirationDateInput: string = '';
    public previousYear: Date;
    public currentYear: Date;
    public expirationDate: Date;
    public expirationDateFormControl = new FormControl();

    constructor(private readonly localizedTexts: I18NextService, @Inject(LOCALE_ID) private locale: string) { }

    ngOnInit(): void {
        this.setupTabs();
        this.initializeFormValues();

        const date = new Date();
        date.setFullYear(date.getFullYear() - 1, 11, 31); // Set to December 31st of the previous year
        this.previousYear = new Date(date);
        this.currentYear = new Date(date.getFullYear() + 1, 11, 31); // December 31st of the current year
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['document'] && !changes['document'].isFirstChange()) {
            this.initializeFormValues();
        }
    }

    private initializeFormValues(): void {
        // Determine if we're in edit mode (document has meaningful data)
        this.isEditMode = !!(
            this.document &&
            (this.document.name ||
                this.document.externalLinkUrl ||
                this.document.documentId ||
                this.document.filesMetaData)
        );

        // Determine document type for edit mode restrictions
        this.isLinkDocument = !!this.document?.externalLinkUrl;
        this.isFileDocument = !!this.document?.filesMetaData && !this.isLinkDocument;

        if (this.document) {
            // Initialize form values from document input
            this.documentName = this.document.name || '';
            this.externalLinkUrl = this.document.externalLinkUrl || '';
            this.isPermanentFile = Boolean(this.document.isPermanent) || false;

            // Initialize file metadata for existing file documents
            if (this.isFileDocument && this.document.filesMetaData) {
                this.fileMetaData = {
                    name: this.document.filesMetaData?.name,
                    size: this.document.filesMetaData?.size,
                };
                // Note: We don't set selectedFile since this is existing file data, not a new upload
            }

            // Only initialize expiry date if it exists in the document
            if (this.document.expiryDate) {
                this.expirationDate = new Date(this.document.expiryDate);
                this._expirationDateInput = this.formatDate(this.expirationDate);
                this.expirationDateFormControl.setValue(this.expirationDate);
            } else {
                // Clear expiry date if document doesn't have one
                this.expirationDate = null;
                this._expirationDateInput = '';
                this.expirationDateFormControl.setValue(null);
            }

            // Set the appropriate tab based on whether it's a file upload or external link
            if (this.document.externalLinkUrl) {
                this.selectedTab = 1; // Link tab
                this.navigationItems.forEach((nav, i) => {
                    nav.active = i === 1;
                });
            }
        } else {
            // Reset edit mode and clear all values when document is empty
            this.isEditMode = false;
            this.isLinkDocument = false;
            this.isFileDocument = false;
            this.documentName = '';
            this.externalLinkUrl = '';
            this.isPermanentFile = false;
            this.expirationDate = null;
            this._expirationDateInput = '';
            this.expirationDateFormControl.setValue(null);
            this.selectedFile = null;
            this.fileMetaData = null;
            this.fileExtensionError = false;

            // Reset tab to upload tab and navigation state
            this.selectedTab = 0;
            this.navigationItems.forEach((nav, i) => {
                nav.active = i === 0; // Set first tab (upload) as active
            });
        }
    }

    private setupTabs(): void {
        this.tabsTitles = [
            this.localizedTexts.t('aumico_ui_document_edit_form_file_upload') as string,
            this.localizedTexts.t('aumico_ui_document_edit_form_add_link') as string,
        ];
        this.navigationItems = [
            { name: 'upload_file', active: true },
            { name: 'add_link', active: false },
        ];
    }

    // === FILE UPLOAD METHODS ===

    public onFileUploadedChanged(event: FileList): void {
        this.fileExtensionError = false;
        this.clearValidationErrors(); // Clear validation errors when file changes
        if (!event || event.length === 0) {
            return;
        }

        const file = event.item(0); // Only handle first file for single upload
        if (!file) {
            return;
        }

        if (this.validateFile(file)) {
            this.selectedFile = file;
            this.fileMetaData = { size: file.size, name: file.name };
            this.fileSizeError.emit(false);

            // Auto-populate document name if empty
            if (!this.documentName) {
                this.documentName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
            }
        }
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer?.files) {
            this.onFileUploadedChanged(event.dataTransfer.files);
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public resetFiles(event: any): void {
        event.target.value = '';
    }

    public removeSelectedFile(): void {
        // Check if document name matches the file name (auto-populated)
        const shouldResetName =
            this.selectedFile && this.documentName === this.selectedFile.name.replace(/\.[^/.]+$/, '');

        this.selectedFile = null;
        this.fileMetaData = null;
        this.fileExtensionError = false;

        // Reset document name if it was auto-populated from the file
        if (shouldResetName) {
            this.documentName = '';
        }
    }

    private validateFile(file: File): boolean {
        const extension = '.' + file.name.split('.').pop();

        // Size validation
        if (this.maxSizeInMb && file.size > this.maxSizeInMb * 1024 * 1024) {
            this.fileSizeError.emit(true);
            return false;
        }

        // Extension validation
        if (this.acceptedExtensions.length > 0 && !this.acceptedExtensions.includes(extension)) {
            this.fileExtensionError = true;
            return false;
        }

        return true;
    }

    // === VALIDATION METHODS ===

    private isValidHttpsUrl(url: string): boolean {
        if (!url || url.trim() === '') {
            return false;
        }

        try {
            const urlObj = new URL(url.trim());

            // Check if protocol is https
            if (urlObj.protocol !== 'https:') {
                return false;
            }

            // Check if hostname exists and is not empty
            if (!urlObj.hostname || urlObj.hostname.trim() === '') {
                return false;
            }

            // Check if hostname contains at least one dot (e.g., example.com)
            // This ensures it's not just a single word like "sadfadsf"
            if (!urlObj.hostname.includes('.')) {
                return false;
            }

            // Optional: Check if hostname has a valid TLD (at least 2 characters after the last dot)
            const parts = urlObj.hostname.split('.');
            const tld = parts[parts.length - 1];
            if (!tld || tld.length < 2) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    private validateForm(): boolean {
        this.clearValidationErrors();

        // Check if document name is provided (required for all cases)
        if (!this.documentName || this.documentName.trim() === '') {
            this.nameRequiredError = true;
            return false;
        }

        if (this.selectedTab === 0) {
            // File upload tab validation
            if (!this.selectedFile && !this.isEditMode) {
                this.fileRequiredError = true;
                return false;
            }
        } else if (this.selectedTab === 1) {
            // Link tab validation
            if (!this.externalLinkUrl || this.externalLinkUrl.trim() === '') {
                this.urlRequiredError = true;
                return false;
            }

            if (!this.isValidHttpsUrl(this.externalLinkUrl)) {
                this.urlValidationError = true;
                return false;
            }
        }

        return true;
    }

    private clearValidationErrors(): void {
        this.urlValidationError = false;
        this.fileRequiredError = false;
        this.urlRequiredError = false;
        this.nameRequiredError = false;
    }

    public get isFormValid(): boolean {
        // Document name is always required
        if (!this.documentName || this.documentName.trim() === '') {
            return false;
        }

        if (this.selectedTab === 0) {
            // File upload tab
            return this.selectedFile !== null || this.isEditMode;
        } else if (this.selectedTab === 1) {
            // Link tab
            return (
                this.externalLinkUrl && this.externalLinkUrl.trim() !== '' && this.isValidHttpsUrl(this.externalLinkUrl)
            );
        }
        return false;
    }

    // === EVENT HANDLERS ===
    public onCancelClick(): void {
        this.onCancel.emit();
    }

    public onUploadClick(): void {
        // Validate form before submission
        if (!this.validateForm()) {
            console.log('Form validation failed');
            return;
        }

        const updatedDocument: Partial<DocumentModel> = {
            ...this.document,
            name: this.documentName,
            isPermanent: this.isPermanentFile,
            // Only include expiration date if permanent file is true
            expiryDate: this.isPermanentFile ? this.expirationDate : null,
        };

        // Only add link URL if we're on the Link tab
        if (this.selectedTab === 1) {
            updatedDocument.externalLinkUrl = this.externalLinkUrl;
        }

        // Emit combined document and file data
        const submissionData: DocumentSubmissionData = {
            document: updatedDocument,
            // Only include file if we're on the Upload File tab
            file: this.selectedTab === 0 ? (this.selectedFile || undefined) : undefined,
            isNew: !this.isEditMode,
        };

        this.documentSubmit.emit(submissionData);
    }

    public onTabClick(item: NavigationItem): void {
        // Prevent tab switching in edit mode
        if (this.isEditMode) {
            return;
        }

        const index = this.navigationItems.findIndex((nav) => nav.name === item.name);
        if (index !== -1) {
            // Clear validation errors when switching tabs
            this.clearValidationErrors();

            // Update active state
            this.navigationItems.forEach((nav, i) => {
                nav.active = i === index;
            });
            this.selectedTab = index;
        }
    }

    // Handle URL input changes to clear validation errors
    public onUrlChange(): void {
        this.clearValidationErrors();
    }

    // Handle document name input changes to clear validation errors
    public onNameChange(): void {
        this.clearValidationErrors();
    }

    // Handle URL blur event to automatically add https:// prefix and convert http to https
    public onUrlBlur(): void {
        if (this.externalLinkUrl && this.externalLinkUrl.trim()) {
            const trimmedUrl = this.externalLinkUrl.trim();

            // Convert http:// to https://
            if (trimmedUrl.startsWith('http://')) {
                this.externalLinkUrl = trimmedUrl.replace('http://', 'https://');
            }
            // Add https:// prefix if no protocol is present
            else if (!trimmedUrl.startsWith('https://')) {
                this.externalLinkUrl = 'https://' + trimmedUrl;
            }
        }
        this.clearValidationErrors();
    }

    public openDateFlyout(buttonComponent: HTMLInputElement): void {
        this.dateFlyoutOpened = true;

        const rect = buttonComponent.getBoundingClientRect();
        const flyoutHeight = 450; // Approximate flyout height - adjust based on your actual flyout size
        const flyoutWidth = 300;

        // Calculate initial position below the button
        let top = rect.bottom;
        const left = rect.left;
        const right = window.innerWidth - rect.left - flyoutWidth;

        // Check if flyout would extend beyond bottom of viewport
        if (top + flyoutHeight > window.innerHeight) {
            // Position above the button instead
            top = rect.top - flyoutHeight;

            // If it still doesn't fit above, position at top of viewport with padding
            if (top < 0) {
                top = 10; // 10px padding from top
            }
        }

        // Ensure there's padding from the top if positioned below
        if (top < 10) {
            top = 10;
        }

        // Ensure there's padding from the bottom
        if (top + flyoutHeight > window.innerHeight - 10) {
            top = window.innerHeight - flyoutHeight - 10;
        }

        this.flyoutPosition = { top: top, right: left, left: right };
    }

    public closeDateFlyout(): void {
        this.dateFlyoutOpened = false;
    }

    public updateDate(date: Date): void {
        this.expirationDate = date;
        this._expirationDateInput = this.formatDate(date);
        this.expirationDateFormControl.setValue(date);
        this.dateFlyoutOpened = false;
    }

    get expirationDateInput(): string {
        if (this.expirationDate instanceof Date && isValid(this.expirationDate)) {
            return this.formatDate(this.expirationDate);
        }
        return this._expirationDateInput; // ← This could contain old values
    }

    set expirationDateInput(value: string) {
        this._expirationDateInput = value;
    }

    private formatDate(date: Date): string {
        const isEnglish = this.locale.startsWith('en');
        return format(date, isEnglish ? 'MM/dd/yyyy' : 'dd.MM.yyyy');
    }

    public onPermanentFileToggle(isPermanent: boolean): void {
        this.isPermanentFile = isPermanent;
        // Note: We no longer clear the expiration date when untoggling permanent file
        // The date remains visible, but won't be sent when submitting if permanent is false
    }

    public select3112minus1year(): void {
        const normalized = new Date(Date.UTC(
            this.previousYear.getUTCFullYear(),
            this.previousYear.getUTCMonth(),
            this.previousYear.getUTCDate(),
        ));
        this.updateDate(normalized);
    }

    public select3112thisYear(): void {
        const normalized = new Date(Date.UTC(
            this.currentYear.getUTCFullYear(),
            this.currentYear.getUTCMonth(),
            this.currentYear.getUTCDate(),
        ));
        this.updateDate(normalized);
    }
}
